import { useState } from 'react';
import { AnimatedGraph } from '../components/AnimatedGraph';
import { PaymentButton } from '../components/PaymentButton';

interface DataPoint {
  index: number;
  x: string;
  y: number;
  z?: number;  // Optional second Y value
}

export function AnimatedLineGraph() {
  const [data, setData] = useState<DataPoint[]>([]);
  const [inputText, setInputText] = useState('');
  const [shouldDraw, setShouldDraw] = useState(false);
  const [showPoints, setShowPoints] = useState(true);
  const [animationSpeed, setAnimationSpeed] = useState(0.05);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [axisLabels, setAxisLabels] = useState<{ x: string; y: string; z?: string } | null>(null);
  const [minYLabel, setMinYLabel] = useState('Min Y');
  const [maxYLabel, setMaxYLabel] = useState('Max Y');
  const [minZLabel, setMinZLabel] = useState('Min Z');
  const [maxZLabel, setMaxZLabel] = useState('Max Z');
  const [labelFontSize, setLabelFontSize] = useState(14);
  const [showMinYLabel, setShowMinYLabel] = useState(true);
  const [showMaxYLabel, setShowMaxYLabel] = useState(true);
  const [showMinZLabel, setShowMinZLabel] = useState(true);
  const [showMaxZLabel, setShowMaxZLabel] = useState(true);
  const [pauseOnMinMax, setPauseOnMinMax] = useState(false);
  const [pauseDuration, setPauseDuration] = useState(1000);
  const [captureVideo, setCaptureVideo] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [showWatermark, setShowWatermark] = useState(true);
  const [drawCount, setDrawCount] = useState(0);

  const colorOptions = [
    { value: 'white', label: 'White' },
    { value: 'black', label: 'Black' },
    { value: '#f0f0f0', label: 'Light Grey' },
    { value: '#404040', label: 'Dark Grey' }
  ];

  const parseData = (text: string) => {
    const lines = text.trim().split('\n');
    const parsedData: DataPoint[] = [];
    let index = 0;
    
    // Check if first row contains text in Y column
    const firstLine = lines[0];
    const firstValues = firstLine.split(/[\s,]+/).filter(val => val.trim() !== '');
    const hasHeaderRow = firstValues.length >= 2 && isNaN(parseFloat(firstValues[1]));
    
    // If first row is header, set axis labels and skip it
    if (hasHeaderRow) {
      setAxisLabels({
        x: firstValues[0],
        y: firstValues[1],
        z: firstValues[2]  // Optional Z label
      });
      lines.shift(); // Remove the header row
    } else {
      setAxisLabels(null);
    }
    
    lines.forEach(line => {
      // Split by any combination of spaces, tabs, or commas
      const values = line.split(/[\s,]+/).filter(val => val.trim() !== '');
      if (values.length >= 2) {
        const x = values[0];  // Keep x as string
        const y = parseFloat(values[1]);
        const z = values.length >= 3 ? parseFloat(values[2]) : undefined;
        
        if (!isNaN(y)) {
          parsedData.push({ index, x, y, z });
          index++;
        }
      }
    });
    
    return parsedData;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    setShouldDraw(false);
  };

  const handleUseExampleData = async () => {
    try {
      const response = await fetch('/exampleOilBTCData.txt');
      if (!response.ok) {
        throw new Error('Failed to load example data');
      }
      const exampleText = await response.text();
      setInputText(exampleText);
      setAxisLabels(null); // Let the parser detect headers automatically
    } catch (error) {
      console.error('Error loading example data:', error);
      // Fallback to hardcoded data if file loading fails
      const fallbackText = `Date	WestTexasCrude	Bitcoin
2024-08-01	76.31	65358
2024-08-02	73.52	61415
2024-08-05	72.94	53991
2024-08-06	73.2	56034
2024-08-07	75.23	55027
2024-08-08	76.19	61710`;
      setInputText(fallbackText);
      setAxisLabels(null);
    }
  };

  const handleDrawClick = (captureVideoParam = false) => {
    const parsedData = parseData(inputText);
    setData(parsedData);
    setShouldDraw(true);
    if (captureVideoParam) {
      setCaptureVideo(true);
    }
    setDrawCount(drawCount + 1);
  };

  const handleRecordingComplete = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    
    // Determine the correct extension based on the blob type and MIME type
    let extension = 'mp4'; // Default to MP4 for Twitter/X compatibility
    if (blob.type.includes('webm')) {
      extension = 'webm';
    } else if (blob.type.includes('mp4') || blob.type.includes('h264') || blob.type.includes('avc1')) {
      extension = 'mp4';
    }
    
    const filename = `animated-line-graph.${extension}`;
    console.log('Downloading video as:', filename, 'blob type:', blob.type, 'extension:', extension);
    
    // Check if we're on iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      // For iOS Safari, create a temporary link and trigger the share sheet
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      
      // Trigger the download first
      a.click();
      
      // After a short delay, show the share sheet
      setTimeout(() => {
        // Create a new blob URL for sharing
        const shareUrl = URL.createObjectURL(blob);
        
        // Check if Web Share API is available
        if (navigator.share && navigator.canShare) {
          const shareData = {
            title: 'Animated Line Graph',
            text: 'Check out this animated line graph I created!',
            url: shareUrl,
            files: [new File([blob], filename, { type: blob.type })]
          };
          
          if (navigator.canShare(shareData)) {
            navigator.share(shareData)
              .then(() => {
                console.log('Share sheet was shown successfully');
                URL.revokeObjectURL(shareUrl);
              })
              .catch((error) => {
                console.log('Error showing share sheet:', error);
                URL.revokeObjectURL(shareUrl);
              });
          } else {
            console.log('Web Share API not supported for files');
            URL.revokeObjectURL(shareUrl);
          }
        } else {
          console.log('Web Share API not available');
          URL.revokeObjectURL(shareUrl);
        }
      }, 1000); // 1 second delay to ensure download completes
      
      // Clean up the temporary link
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 2000);
    } else {
      // For other browsers, use the standard download approach
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    
    setCaptureVideo(false); // Reset captureVideo after download
  };

  const handlePaymentSuccess = () => {
    setShowWatermark(false);
    setIsPaid(true);
  };

  const handlePaymentExpire = () => {
    setShowWatermark(true);
    setIsPaid(false);
    // Don't reset any other state
  };

  return (
    <div className="app-container">
      <h1>Animated Line Graph</h1>
      
      {/* Text entry box - centered */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'center'
        }}>
          <textarea
            value={inputText}
            onChange={handleInputChange}
            placeholder="Enter data in format:&#10;X-Axis Y-Axis Z-Axis&#10;1 2 3&#10;3 4 5&#10;5 6 7&#10;Or:&#10;1,2,3&#10;3,4,5&#10;5,6,7"
            rows={10}
            cols={50}
            style={{
              padding: '12px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: 'monospace',
              resize: 'vertical',
              overflowX: 'auto',
              whiteSpace: 'nowrap'
            }}
          />
          <button
            onClick={handleUseExampleData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Use Example Data
          </button>
        </div>
      </div>

      {/* Controls box - centered */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        marginBottom: '20px'
      }}>
        <div className="mobile-controls-grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '15px',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="showPoints" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Show Points
            </label>
            <input
              type="checkbox"
              id="showPoints"
              checked={showPoints}
              onChange={(e) => setShowPoints(e.target.checked)}
              className="mobile-checkbox"
            />
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="animationSpeed" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Animation Speed
            </label>
            <input
              type="range"
              id="animationSpeed"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              min="0.01"
              max="0.2"
              step="0.01"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{animationSpeed.toFixed(2)}</span>
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="backgroundColor" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Background
            </label>
            <select
              id="backgroundColor"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="mobile-select"
            >
              {colorOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="minYLabel" 
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Min Y Label
            </label>
            <input
              type="text"
              id="minYLabel"
              value={minYLabel}
              onChange={(e) => setMinYLabel(e.target.value)}
              style={{
                padding: '4px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <input
              type="checkbox"
              id="showMinYLabel"
              checked={showMinYLabel}
              onChange={(e) => setShowMinYLabel(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer'
              }}
            />
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="maxYLabel" 
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Max Y Label
            </label>
            <input
              type="text"
              id="maxYLabel"
              value={maxYLabel}
              onChange={(e) => setMaxYLabel(e.target.value)}
              style={{
                padding: '4px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <input
              type="checkbox"
              id="showMaxYLabel"
              checked={showMaxYLabel}
              onChange={(e) => setShowMaxYLabel(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer'
              }}
            />
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="minZLabel" 
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Min Z Label
            </label>
            <input
              type="text"
              id="minZLabel"
              value={minZLabel}
              onChange={(e) => setMinZLabel(e.target.value)}
              style={{
                padding: '4px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <input
              type="checkbox"
              id="showMinZLabel"
              checked={showMinZLabel}
              onChange={(e) => setShowMinZLabel(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer'
              }}
            />
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="maxZLabel" 
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Max Z Label
            </label>
            <input
              type="text"
              id="maxZLabel"
              value={maxZLabel}
              onChange={(e) => setMaxZLabel(e.target.value)}
              style={{
                padding: '4px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <input
              type="checkbox"
              id="showMaxZLabel"
              checked={showMaxZLabel}
              onChange={(e) => setShowMaxZLabel(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer'
              }}
            />
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="labelFontSize" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Label Font Size
            </label>
            <input
              type="number"
              id="labelFontSize"
              value={labelFontSize}
              onChange={(e) => setLabelFontSize(parseInt(e.target.value) || 14)}
              min="8"
              max="32"
              step="1"
              className="mobile-number-input"
            />
          </div>
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="pauseOnMinMax" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Pause on Min/Max
            </label>
            <input
              type="checkbox"
              id="pauseOnMinMax"
              checked={pauseOnMinMax}
              onChange={(e) => setPauseOnMinMax(e.target.checked)}
              className="mobile-checkbox"
            />
          </div>
          
          {pauseOnMinMax && (
            <div className="mobile-control-item" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px'
            }}>
              <label 
                htmlFor="pauseDuration" 
                className="mobile-label"
                style={{ 
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                Pause Duration (ms)
              </label>
              <input
                type="number"
                id="pauseDuration"
                value={pauseDuration}
                onChange={(e) => setPauseDuration(parseInt(e.target.value))}
                min="100"
                max="5000"
                step="100"
                className="mobile-number-input"
              />
            </div>
          )}
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: '1px solid #dee2e6'
          }}>
            <PaymentButton 
              onPaymentSuccess={handlePaymentSuccess} 
              onPaymentExpire={handlePaymentExpire}
              shouldDraw={shouldDraw}
              drawCount={drawCount}
            />
          </div>
        </div>
      </div>

      {/* Draw buttons - centered */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        gap: '20px',
        marginBottom: '20px'
      }}>
        <button 
          onClick={() => handleDrawClick()}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Draw Graph
        </button>
        <button 
          onClick={() => handleDrawClick(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Draw Graph and Capture Video
        </button>
      </div>

      {/* Graph container */}
      <div className="graph-container" style={{ display: 'flex', justifyContent: 'center' }}>
        <AnimatedGraph 
          data={shouldDraw ? data : []} 
          width={800} 
          height={600} 
          showPoints={showPoints}
          animationSpeed={animationSpeed}
          backgroundColor={backgroundColor}
          axisLabels={axisLabels}
          minYLabel={minYLabel}
          maxYLabel={maxYLabel}
          minZLabel={minZLabel}
          maxZLabel={maxZLabel}
          labelFontSize={labelFontSize}
          showMinYLabel={showMinYLabel}
          showMaxYLabel={showMaxYLabel}
          showMinZLabel={showMinZLabel}
          showMaxZLabel={showMaxZLabel}
          pauseOnMinMax={pauseOnMinMax}
          pauseDuration={pauseDuration}
          onRecordingComplete={handleRecordingComplete}
          captureVideo={captureVideo}
          showWatermark={showWatermark}
        />
      </div>
    </div>
  );
} 